import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';
import { useCountUp } from '../../hooks/useCountUp';

// Tone -> token-driven color classes for the icon chip and accents.
const TONES = {
  primary: { chip: 'bg-primary/10 text-primary', ring: 'stroke-primary' },
  success: { chip: 'bg-success/10 text-success', ring: 'stroke-success' },
  warning: { chip: 'bg-warning/12 text-amber-600', ring: 'stroke-warning' },
  danger: { chip: 'bg-danger/10 text-danger', ring: 'stroke-danger' },
  info: { chip: 'bg-info/10 text-info', ring: 'stroke-info' },
  purple: { chip: 'bg-purple/10 text-purple', ring: 'stroke-purple' },
  slate: { chip: 'bg-muted text-muted-foreground', ring: 'stroke-slate-400' },
};

function ProgressRing({ value = 0, tone = 'primary', size = 44 }) {
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (pct / 100) * c;
  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="stroke-muted" fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        strokeWidth={stroke}
        strokeLinecap="round"
        className={cn('transition-all duration-700 ease-out', (TONES[tone] || TONES.primary).ring)}
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
      />
    </svg>
  );
}

/**
 * Premium KPI/stat card with animated count-up, optional trend delta and ring.
 * Backwards-friendly: all props optional; renders cleanly with just label+value.
 */
export function StatCard({
  label,
  value = 0,
  suffix = '',
  prefix = '',
  icon: Icon,
  tone = 'primary',
  deltaPct,
  deltaLabel = 'vs last period',
  ring,
  decimals = 0,
  hint,
  onClick,
  className,
  animate = true,
}) {
  const numeric = typeof value === 'number';
  const animated = useCountUp(numeric ? value : 0, { decimals, duration: animate ? 900 : 0 });
  const shown = numeric ? animated.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : value;
  const t = TONES[tone] || TONES.primary;

  const hasDelta = typeof deltaPct === 'number' && Number.isFinite(deltaPct);
  const up = hasDelta && deltaPct > 0;
  const flat = hasDelta && deltaPct === 0;
  const DeltaIcon = flat ? Minus : up ? TrendingUp : TrendingDown;

  return (
    <Card
      className={cn(
        'group relative overflow-hidden',
        onClick && 'cursor-pointer card-interactive',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground truncate">{label}</p>
            <p className="mt-2 text-[26px] font-bold leading-none tracking-tight tnum text-foreground">
              {prefix}{shown}{suffix}
            </p>
            {hint && <p className="mt-1.5 text-xs text-muted-foreground truncate">{hint}</p>}
          </div>
          {typeof ring === 'number' ? (
            <div className="relative grid place-items-center">
              <ProgressRing value={ring} tone={tone} />
              <span className="absolute text-[11px] font-semibold tnum text-foreground">{Math.round(ring)}%</span>
            </div>
          ) : Icon ? (
            <div className={cn('grid h-10 w-10 place-items-center rounded-xl transition-transform group-hover:scale-105', t.chip)}>
              <Icon className="h-[18px] w-[18px]" />
            </div>
          ) : null}
        </div>

        {hasDelta && (
          <div className="mt-3 flex items-center gap-1.5 text-xs">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium',
                flat ? 'bg-muted text-muted-foreground' : up ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
              )}
            >
              <DeltaIcon className="h-3 w-3" />
              {Math.abs(deltaPct).toFixed(1)}%
            </span>
            <span className="text-muted-foreground truncate">{deltaLabel}</span>
          </div>
        )}
      </CardContent>
      {/* Brand accent bar on hover */}
      <span className="absolute inset-x-0 bottom-0 h-0.5 scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
    </Card>
  );
}

export default StatCard;
